import React, { useEffect, useState } from "react";
import { createPortal } from "react-dom";
import { Container } from "react-bootstrap";
import { useDispatch, useSelector } from "react-redux";
import { fetchRoles, toggleRoleStatus } from "../../../redux/slice/adminRoleSlice";
import { useLoader } from "../../../context/LoaderContext";
import ListSection from "./ListSection";
import ManagePermissionModal from "./ManagePermissionModal";
import { usePermission } from "../../../hooks/usePermission";
import { useToast } from "../../../context/TostContext";
import RoleModal from "./RoleModal";

const MODULE_SLUG = "admin_roles";

const Roles = () => {
  const dispatch = useDispatch();
  const { setLoading } = useLoader();
  const headerPortalTarget = document.getElementById("header-portal-target");

  const headerContent = (
    <div className="d-flex align-items-center justify-content-between w-100">
      <h4 className="fw-semibold mb-0 text-primary fs-lg">
        <span className="text-muted fw-normal">Admin Management / </span>Roles
      </h4>
    </div>
  );
  const toast = useToast();

  const { roles = [], isLoading } = useSelector((state) => state.adminRoles || {});

  const [openModal, setOpenModal] = useState(false);
  const [openPermissionModal, setOpenPermissionModal] = useState(false);
  const [selectedRole, setSelectedRole] = useState(null);
  const [searchTerm, setSearchTerm] = useState("");
  const [isView, setIsView] = useState(false);

  const { hasPermission } = usePermission();
  const canCreate = hasPermission(MODULE_SLUG, "create");
  const canUpdate = hasPermission(MODULE_SLUG, "update");
  const canDelete = hasPermission(MODULE_SLUG, "delete");

  useEffect(() => {
    if (selectedRole) {
      const updatedRole = roles.find((r) => r.id === selectedRole.id);
      if (updatedRole && updatedRole !== selectedRole) {
        setSelectedRole(updatedRole);
      }
    }
  }, [roles, selectedRole]);

  useEffect(() => {
    console.log("Roles from Redux:", roles);
  }, [roles]);

  useEffect(() => {
    setLoading(true);
    dispatch(fetchRoles()).finally(() => setLoading(false));
  }, [dispatch, setLoading]);

  const handleAddRole = () => {
    setSelectedRole(null);
    setOpenModal(true);
  };

  const handleEditRole = (role) => {
    if (role.is_system) {
      toast.error("System roles cannot be modified");
      return;
    }
    setSelectedRole(role);
    setIsView(false);
    setOpenModal(true);
  };

  const handleViewRole = (role) => {
    setSelectedRole(role);
    setIsView(true);
    setOpenModal(true);
  };

  const handleManagePermissions = (role) => {
    setSelectedRole(role);
    setOpenPermissionModal(true);
  };

  const handleToggleStatus = (role) => {
    setLoading(true);
    dispatch(toggleRoleStatus(role.id))
      .then((res) => {
        if (res.error) {
          toast.error(res.payload?.message || "Failed to update role status");
        } else {
          toast.success(res.payload?.message || "Role status updated successfully");
        }
      })
      .finally(() => setLoading(false));
  };

  return (
    <>
      {headerPortalTarget && createPortal(headerContent, headerPortalTarget)}
      <Container fluid className="px-0">
        <div className="bg-white rounded-2 overflow-hidden common-shadow">
          <ListSection
            data={roles}
            isLoading={isLoading}
            searchTerm={searchTerm}
            setSearchTerm={setSearchTerm}
            onAdd={canCreate ? handleAddRole : null}
            onEdit={canUpdate ? handleEditRole : null}
            onView={handleViewRole}
            onDelete={canDelete ? handleToggleStatus : null}
            onManagePermissions={canUpdate ? handleManagePermissions : null}
          />
        </div>

        <RoleModal
          isOpen={openModal}
          onClose={() => {
            setOpenModal(false);
            setIsView(false);
          }}
          title={isView ? "View Role" : selectedRole ? "Edit Role" : "Add New Role"}
          role={selectedRole}
          isView={isView}
          size="sm"
          onSuccess={() => dispatch(fetchRoles())}
        />

        <ManagePermissionModal
          isOpen={openPermissionModal}
          onClose={() => setOpenPermissionModal(false)}
          title={`Manage Permissions - ${selectedRole?.name || ""}`}
          size="xl"
          role={selectedRole}
          onSuccess={() => dispatch(fetchRoles())}
          className="permission-grid-table"
        />
      </Container>
    </>
  );
};

export default Roles;
